import React, { useRef, useState } from "react";
import { Button } from "antd";

/** Captura una imagen de la firma. El servidor debe validar la imagen recibida. */
export default function FirmaManuscrita({ onChange }: { onChange: (png: string | null) => void }) {
  const lienzo = useRef<HTMLCanvasElement>(null);
  const activo = useRef<number | null>(null);
  const anterior = useRef<{ x: number; y: number } | null>(null);
  const trazado = useRef(false);
  const [tieneFirma, setTieneFirma] = useState(false);

  const punto = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    if (!rect.width || !rect.height) return null;
    return {
      x: Math.max(0, Math.min(720, ((e.clientX - rect.left) / rect.width) * 720)),
      y: Math.max(0, Math.min(240, ((e.clientY - rect.top) / rect.height) * 240)),
    };
  };

  const empezar = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (activo.current !== null || (e.pointerType === "mouse" && e.button !== 0)) return;
    const p = punto(e);
    if (!p) return;
    e.preventDefault();
    activo.current = e.pointerId;
    anterior.current = p;
    trazado.current = false;
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };

  const dibujar = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (activo.current !== e.pointerId || !anterior.current) return;
    const p = punto(e);
    const ctx = e.currentTarget.getContext("2d");
    if (!p || !ctx) return;
    if (p.x === anterior.current.x && p.y === anterior.current.y) return;
    ctx.strokeStyle = "#17213a";
    ctx.lineWidth = 3;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.beginPath();
    ctx.moveTo(anterior.current.x, anterior.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    anterior.current = p;
    trazado.current = true;
  };

  const terminar = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (activo.current !== e.pointerId) return;
    activo.current = null;
    anterior.current = null;
    if (trazado.current) {
      setTieneFirma(true);
      onChange(e.currentTarget.toDataURL("image/png"));
    }
    trazado.current = false;
  };

  const limpiar = () => {
    const canvas = lienzo.current;
    canvas?.getContext("2d")?.clearRect(0, 0, canvas.width, canvas.height);
    activo.current = null;
    anterior.current = null;
    trazado.current = false;
    setTieneFirma(false);
    onChange(null);
  };

  return (
    <div>
      <p id="firma-instrucciones">Dibuja tu firma con el dedo, lápiz o mouse.</p>
      <canvas
        ref={lienzo}
        width={720}
        height={240}
        aria-label="Área para dibujar la firma"
        aria-describedby="firma-instrucciones"
        onPointerDown={empezar}
        onPointerMove={dibujar}
        onPointerUp={terminar}
        onPointerCancel={terminar}
        onLostPointerCapture={terminar}
        style={{
          width: "100%",
          maxWidth: 720,
          aspectRatio: "3 / 1",
          border: "1px solid #b7b7c5",
          borderRadius: 6,
          touchAction: "none",
          background: "white",
        }}
      />
      <div>
        <Button htmlType="button" onClick={limpiar} disabled={!tieneFirma}>
          Limpiar firma
        </Button>
      </div>
    </div>
  );
}
